/**
 * Bella's copy, in Vietnamese and English.
 *
 * Vietnamese is the source of truth: it is the default and the primary
 * audience. English is typed against it, so adding a Vietnamese key without an
 * English one is a compile error rather than a string that silently falls back.
 *
 * Keys are flat and dot-namespaced by screen. Interpolation is `{name}`.
 * Vietnamese does not inflect for plural, so anywhere English needs one the
 * value is written to read correctly with a leading count in both.
 */

export const vi = {
  // --- generic ---
  'app.tagline': 'Trò chuyện, nhóm và gọi điện với những người bạn quan tâm.',
  'common.cancel': 'Huỷ',
  'common.save': 'Lưu',
  'common.saved': 'Đã lưu.',
  'common.delete': 'Xoá',
  'common.remove': 'Gỡ',
  'common.add': 'Thêm',
  'common.back': 'Quay lại',
  'common.close': 'Đóng',
  'common.retry': 'thử lại',
  'common.loading': 'Đang tải…',
  'common.somethingWrong': 'Đã có lỗi xảy ra. Vui lòng thử lại.',
  'common.you': '(bạn)',
  'common.confirm': 'Xác nhận',

  // --- navigation ---
  'nav.home': 'Trang chủ',
  'nav.chats': 'Tin nhắn',
  'nav.people': 'Danh bạ',
  'nav.you': 'Cài đặt',

  // --- sign in ---
  'auth.email': 'Email',
  'auth.phone': 'Số điện thoại',
  'auth.password': 'Mật khẩu',
  'auth.yourName': 'Tên của bạn',
  'auth.signIn': 'Đăng nhập',
  'auth.createAccount': 'Tạo tài khoản',
  'auth.haveAccount': 'Tôi đã có tài khoản',
  'auth.newAccount': 'Tạo tài khoản mới',
  'auth.forgotPassword': 'Quên mật khẩu?',
  'auth.backToSignIn': 'Quay lại đăng nhập',
  'auth.noAccount': 'Chưa có tài khoản?',
  'auth.hasAccount': 'Đã có tài khoản?',
  'auth.signUpNow': 'Đăng ký',
  'auth.signInNow': 'Đăng nhập',
  'auth.resetIntro': 'Nhập email của bạn và chúng tôi sẽ gửi liên kết đặt lại mật khẩu.',
  'auth.sendResetLink': 'Gửi liên kết đặt lại',
  'auth.resetSent':
    'Nếu địa chỉ đó có tài khoản, liên kết đặt lại đang được gửi đi. Hãy kiểm tra email.',
  'auth.checkEmail': 'Kiểm tra email để lấy liên kết xác nhận, rồi đăng nhập.',
  'auth.continueWithGoogle': 'Tiếp tục với Google',
  'auth.continueWithApple': 'Tiếp tục với Apple',
  'auth.continueWithFacebook': 'Tiếp tục với Facebook',
  'auth.continueWithMicrosoft': 'Tiếp tục với Microsoft',
  'auth.or': 'hoặc',
  'auth.continue': 'Tiếp tục',
  'auth.smsNotice': 'Chúng tôi sẽ nhắn mã 6 chữ số. Áp dụng cước phí thông thường.',
  'auth.codeSentTo': 'Nhập mã đã gửi tới {phone}',
  'auth.verify': 'Xác minh',
  'auth.differentNumber': 'Dùng số khác',
  'auth.terms': 'Khi tiếp tục, bạn đồng ý với Điều khoản và Chính sách quyền riêng tư.',
  'auth.passwordMin': 'Ít nhất 8 ký tự',
  'auth.signingIn': 'Đang đăng nhập…',
  'auth.confirming': 'Đang xác nhận…',
  'auth.linkExpired': 'Liên kết đã hết hạn hoặc đã được dùng. Hãy yêu cầu liên kết mới.',
  'auth.linkIncomplete': 'Liên kết thiếu thông tin. Hãy yêu cầu liên kết mới và thử lại.',
  'auth.linkStale': 'Liên kết đăng nhập đã hết hạn. Vui lòng thử lại.',
  'auth.newPasswordTitle': 'Chọn mật khẩu mới',
  'auth.newPasswordIntro':
    'Bạn đang đăng nhập bằng {email}. Đặt mật khẩu ở đây sẽ thay thế mật khẩu cũ ở mọi nơi.',
  'auth.newPassword': 'Mật khẩu mới',
  'auth.confirmPassword': 'Xác nhận mật khẩu',
  'auth.passwordsDiffer': 'Hai mật khẩu không khớp.',
  'auth.savePassword': 'Lưu mật khẩu',
  'auth.needResetLink':
    'Trang này cần một liên kết đặt lại hợp lệ. Hãy yêu cầu liên kết mới và mở từ email.',
  'auth.providerDisabled': 'Cách đăng nhập đó chưa được bật trên máy chủ này. Hãy dùng email.',
  'auth.badCredentials': 'Email và mật khẩu không khớp với tài khoản nào.',
  'auth.alreadyRegistered': 'Địa chỉ email này đã có tài khoản. Hãy thử đăng nhập.',
  'auth.passwordTooShort': 'Vui lòng chọn mật khẩu có ít nhất 8 ký tự.',
  'auth.offline': 'Không kết nối được máy chủ. Kiểm tra mạng rồi thử lại.',

  // --- home / feed ---
  'home.title': 'Trang chủ',
  'home.composerPlaceholder': 'Bạn đang nghĩ gì?',
  'home.post': 'Đăng',
  'home.visibility': 'Ai có thể xem',
  'home.visibilityFriends': 'Bạn bè',
  'home.visibilityPublic': 'Mọi người',
  'home.visibilityPrivate': 'Chỉ mình tôi',
  'home.emptyTitle': 'Bảng tin còn trống',
  'home.emptyHint': 'Theo dõi vài người và bài viết của họ sẽ hiện ở đây.',
  'home.findPeople': 'Tìm mọi người',
  'home.removeImage': 'Gỡ ảnh',

  // --- post detail / comments ---
  'post.title': 'Bài viết',
  'post.commentsHeading': '{count} bình luận',
  'post.noComments': 'Chưa có bình luận nào.',
  'post.beFirst': 'Hãy là người bình luận đầu tiên.',
  'post.commentPlaceholder': 'Viết bình luận…',
  'post.send': 'Gửi',
  'post.deleteComment': 'Xoá bình luận',
  'post.deleteCommentConfirm': 'Xoá bình luận này?',
  'post.notFound': 'Không tìm thấy bài viết',
  'post.notFoundHint': 'Có thể bài viết đã bị xoá, hoặc bạn không có quyền xem.',
  'post.backHome': 'Về trang chủ',
  'post.like': 'Thích',
  'post.comment': 'Bình luận',

  // --- chats ---
  'chats.title': 'Tin nhắn',
  'chats.newGroup': 'Nhóm mới',
  'chats.newChat': 'Trò chuyện mới',
  'chats.emptyTitle': 'Chưa có cuộc trò chuyện nào',
  'chats.emptyHint': 'Tìm ai đó theo tên hoặc số điện thoại để bắt đầu.',
  'chats.sayHello': 'Chào một câu nào',
  'chats.photo': '📷 Ảnh',
  'chats.video': '🎬 Video',
  'chats.file': '📎 Tệp',
  'chats.message': 'Tin nhắn',
  'chats.typing': '{names} đang nhập…',
  'chats.members': '{count} thành viên',
  'chats.deletedMessage': 'Tin nhắn đã được xoá',
  'chats.sending': 'đang gửi',
  'chats.edited': 'đã sửa',
  'chats.replyingTo': 'Đang trả lời {name}',
  'chats.cancelReply': 'Huỷ trả lời',
  'chats.originalMessage': 'Tin nhắn gốc',
  'chats.messageActions': 'Tuỳ chọn tin nhắn',
  'chats.reply': 'Trả lời',
  'chats.copyText': 'Sao chép',
  'chats.reactWith': 'Bày tỏ cảm xúc {emoji}',
  'chats.voiceCall': 'Gọi thoại',
  'chats.videoCall': 'Gọi video',
  'chats.send': 'Gửi',
  'chats.attach': 'Đính kèm',

  // --- voice notes ---
  'voice.record': 'Ghi âm',
  'voice.stopAndSend': 'Dừng và gửi',
  'voice.readyToSend': 'Sẵn sàng gửi',
  'voice.play': 'Phát',
  'voice.pause': 'Tạm dừng',
  'voice.message': '🎤 Tin nhắn thoại',
  'voice.permissionDenied': 'Bella cần quyền dùng micro để ghi âm.',
  'voice.unsupported': 'Trình duyệt này không ghi âm được.',

  // --- shared media ---
  'media.title': 'Ảnh, video và tệp',
  'media.tabMedia': 'Phương tiện',
  'media.tabFiles': 'Tệp',
  'media.emptyMediaTitle': 'Chưa có ảnh hay video',
  'media.emptyMediaHint': 'Ảnh và video được chia sẻ trong cuộc trò chuyện này sẽ xuất hiện ở đây.',
  'media.emptyFilesTitle': 'Chưa có tệp nào',
  'media.emptyFilesHint': 'Tệp được chia sẻ trong cuộc trò chuyện này sẽ xuất hiện ở đây.',
  'media.file': 'Tệp',
  'media.previous': 'Ảnh trước',
  'media.next': 'Ảnh tiếp theo',
  'media.viewFullscreen': 'Xem toàn màn hình',

  // --- conversation info ---
  'info.sharedMedia': 'Ảnh, video và tệp',
  'info.groupTitle': 'Thông tin nhóm',
  'info.chatTitle': 'Thông tin trò chuyện',
  'info.directMessage': 'Tin nhắn riêng',
  'info.viewProfile': 'Xem trang cá nhân',
  'info.groupName': 'Tên nhóm',
  'info.notifications': 'Thông báo',
  'info.notifyOn': 'Bạn sẽ nhận thông báo tin nhắn mới.',
  'info.mutedUntil': 'Đã tắt tiếng đến {time}',
  'info.unmute': 'Bật lại thông báo',
  'info.mute8h': 'Tắt tiếng 8 giờ',
  'info.mute1w': 'Tắt tiếng 1 tuần',
  'info.membersHeading': '{count} thành viên',
  'info.makeAdmin': 'Đặt làm quản trị',
  'info.demote': 'Bỏ quản trị',
  'info.addPeople': 'Thêm người',
  'info.searchPeople': 'Tìm theo tên hoặc số điện thoại',
  'info.allAlreadyIn': 'Những người tìm thấy đều đã ở trong nhóm.',
  'info.noOneFound': 'Không tìm thấy ai.',
  'info.leaveGroup': 'Rời nhóm',
  'info.leaveConfirm': 'Rời nhóm này? Bạn sẽ không nhận được tin nhắn của nhóm nữa.',
  'info.ownerCannotLeave':
    'Bạn đã tạo nhóm này nên không thể rời đi — nhóm sẽ không còn ai quản lý.',
  'info.removeConfirm': 'Gỡ {name} khỏi nhóm?',
  'info.blockPerson': 'Chặn {name}',
  'info.blockConfirm': 'Chặn {name}? Họ sẽ không thể tìm thấy hoặc nhắn tin cho bạn.',
  'info.notFound': 'Không tìm thấy cuộc trò chuyện',
  'info.notFoundHint': 'Có thể nó đã bị xoá, hoặc bạn đã rời đi.',
  'info.backToChats': 'Về danh sách trò chuyện',
  'info.roleOwner': 'Chủ nhóm',
  'info.roleAdmin': 'Quản trị',

  // --- new group ---
  'group.newTitle': 'Nhóm mới',
  'group.create': 'Tạo',
  'group.namePlaceholder': 'Tên nhóm',
  'group.addPlaceholder': 'Thêm người theo tên hoặc số điện thoại',
  'group.removePerson': 'Bỏ {name}',

  // --- people ---
  'friends.add': 'Kết bạn',
  'friends.accept': 'Đồng ý',
  'friends.decline': 'Từ chối',
  'friends.requestSent': 'Đã gửi lời mời',
  'friends.areFriends': 'Bạn bè',
  'friends.unfriend': 'Hủy kết bạn',
  'friends.unfriendConfirm': 'Hủy kết bạn với {name}?',
  'friends.requestsTitle': 'Lời mời kết bạn ({n})',
  'friends.yoursTitle': 'Bạn bè của bạn',
  'friends.yoursTitleCount': 'Bạn bè của bạn ({n})',
  'friends.searchResults': 'Kết quả tìm kiếm',
  'friends.noneTitle': 'Chưa có bạn bè',
  'friends.noneHint':
    'Tìm theo tên, @tên người dùng hoặc số điện thoại để gửi lời mời kết bạn đầu tiên.',
  'friends.mutualCount': '{n} bạn chung',
  'friends.mutualCountOne': '1 bạn chung',
  'friends.mutualCountZero': 'Không có bạn chung',

  'people.title': 'Danh bạ',
  'people.searchPlaceholder': 'Tên, @tên người dùng hoặc số điện thoại',
  'people.phoneNotice': 'Số điện thoại chỉ khớp chính xác và chỉ với người cho phép.',
  'people.noneTitle': 'Không tìm thấy ai',
  'people.noneHintPhone':
    'Không ai dùng số đó trên Bella, hoặc họ đã tắt tìm kiếm theo số điện thoại.',
  'people.noneHintName': 'Thử cách viết khác, hoặc tìm theo @tên người dùng.',
  'people.matchedByPhone': 'khớp theo số điện thoại',
  'people.message': 'Nhắn tin',
  'people.opening': 'Đang mở…',

  // --- profile ---
  'profile.message': 'Nhắn tin',
  'profile.call': 'Gọi',
  'profile.video': 'Video',
  'profile.noPostsTitle': 'Chưa có bài viết nào',
  'profile.noPostsHint': 'Khi người này đăng bài, bài sẽ hiện ở đây.',
  'profile.noPostsStrangerHint':
    'Hầu hết bài viết chỉ dành cho bạn bè. Kết bạn để xem được nhiều hơn.',
  'profile.noPostsYouTitle': 'Bạn chưa đăng gì',
  'profile.noPostsYouHint': 'Bài viết của bạn sẽ hiện ở đây.',
  'profile.block': 'Chặn',
  'profile.editProfile': 'Sửa trang cá nhân',
  'profile.unavailable': 'Không thể xem người này.',

  // --- settings ---
  'settings.title': 'Cài đặt',
  'settings.changePhoto': 'Đổi ảnh',
  'settings.displayName': 'Tên hiển thị',
  'settings.username': 'Tên người dùng',
  'settings.usernamePlaceholder': 'Chọn tên người dùng',
  'settings.usernameHint': '3–24 ký tự: chữ cái, chữ số và dấu gạch dưới.',
  'settings.bio': 'Giới thiệu',
  'settings.usernameTaken': 'Tên người dùng này đã có người dùng.',
  'settings.couldNotSave': 'Không lưu được.',
  'settings.privacy': 'Quyền riêng tư',
  'settings.findableByPhone': 'Cho phép tìm bằng số điện thoại',
  'settings.findableByPhoneOn': 'Người biết {phone} có thể tìm thấy bạn.',
  'settings.findableByPhoneOff': 'Thêm số điện thoại để dùng mục này.',
  'settings.findableByUsername': 'Cho phép tìm bằng tên người dùng',
  'settings.findableByUsernameHint': 'Mọi người có thể tìm @tên người dùng của bạn.',
  'settings.password': 'Mật khẩu',
  'settings.changePassword': 'Đổi mật khẩu',
  'settings.newPasswordPlaceholder': 'Mật khẩu mới, ít nhất 8 ký tự',
  'settings.confirmPasswordPlaceholder': 'Xác nhận mật khẩu mới',
  'settings.passwordChanged': 'Đã đổi mật khẩu.',
  'settings.couldNotChange': 'Không đổi được.',
  'settings.signInMethods': 'Cách đăng nhập',
  'settings.signInMethodsHint':
    'Nên có ít nhất hai cách đăng nhập. Nếu mất quyền vào một tài khoản, bạn vẫn còn đường khác.',
  'settings.methodEmail': 'Email và mật khẩu',
  'settings.linkedAs': 'Đã liên kết {who}',
  'settings.addMethod': 'Thêm',
  'settings.removeMethod': 'Gỡ liên kết',
  'settings.removeMethodConfirm':
    'Gỡ liên kết {name}? Bạn sẽ không đăng nhập bằng cách đó được nữa.',
  'settings.lastMethodWarning':
    'Đây là cách đăng nhập duy nhất của bạn. Hãy thêm một cách khác trước khi gỡ, nếu không bạn sẽ mất quyền vào tài khoản.',
  'settings.setPasswordFirst':
    'Bạn chưa đặt mật khẩu. Hãy đặt một mật khẩu để có đường vào dự phòng nếu mất tài khoản mạng xã hội.',
  'settings.blocked': 'Đã chặn',
  'settings.blockedHint': 'Họ không thể tìm thấy, nhắn tin hay gọi cho bạn.',
  'settings.unblock': 'Bỏ chặn',
  'settings.notifications': 'Thông báo',
  'settings.notificationsOn': 'Bật thông báo',
  'settings.notificationsOff': 'Tắt thông báo',
  'settings.notificationsIos':
    'Trên iPhone và iPad, hãy thêm Bella vào Màn hình chính trước — Safari chỉ cho phép thông báo với ứng dụng đã cài.',
  'settings.notificationsUnsupported': 'Trình duyệt này không hỗ trợ thông báo đẩy.',
  'settings.notificationsBlocked':
    'Thông báo đang bị chặn. Hãy bật lại trong cài đặt trang web của trình duyệt.',
  'settings.signOut': 'Đăng xuất',
  'settings.language': 'Ngôn ngữ',
  'settings.appearance': 'Giao diện',
  'theme.light': 'Sáng',
  'theme.dark': 'Tối',

  // --- install ---
  'install.title': 'Cài đặt Bella',
  'install.hint': 'Thêm vào màn hình chính để mở nhanh hơn và nhận thông báo.',
  'install.button': 'Cài đặt ứng dụng',
  'install.installed': 'Bella đã được cài trên thiết bị này.',
  'install.iosTitle': 'Cài trên iPhone hoặc iPad',
  'install.iosStep1': 'Chạm nút Chia sẻ ở thanh dưới của Safari.',
  'install.iosStep2': 'Chọn “Thêm vào MH chính”.',
  'install.dismiss': 'Để sau',

  // --- calls ---
  'call.ringing': 'Đang đổ chuông…',
  'call.connecting': 'Đang kết nối…',
  'call.preparing': 'Đang chuẩn bị…',
  'call.ended': 'Đã kết thúc',
  'call.incomingVideo': 'Cuộc gọi video đến',
  'call.incomingVoice': 'Cuộc gọi thoại đến',
  'call.accept': 'Trả lời',
  'call.decline': 'Từ chối',
  'call.mute': 'Tắt tiếng',
  'call.unmute': 'Bật tiếng',
  'call.stopVideo': 'Tắt video',
  'call.startVideo': 'Bật video',
  'call.shareScreen': 'Chia sẻ màn hình',
  'call.endCall': 'Kết thúc',
  'call.unavailable': 'Cuộc gọi này không còn khả dụng.',
  'call.nobodyElse': 'Không còn ai khác trong cuộc trò chuyện này.',
  'call.needsPermission': 'Bella cần quyền dùng micro và camera để gọi.',
  'call.couldNotConnect':
    'Không kết nối được cuộc gọi. Có thể do mạng của một trong hai bên chặn kết nối trực tiếp.',

  // --- update prompt ---
  'update.ready': 'Đã có phiên bản Bella mới.',
  'update.later': 'Để sau',
  'update.reload': 'Tải lại',

  // --- presence ---
  'presence.online': 'đang hoạt động',
  'presence.away': 'vắng mặt',
  'presence.offline': 'ngoại tuyến',
} as const

export type TranslationKey = keyof typeof vi

export const en: Record<TranslationKey, string> = {
  'app.tagline': 'Chat, groups and calls with the people you care about.',
  'common.cancel': 'Cancel',
  'common.save': 'Save',
  'common.saved': 'Saved.',
  'common.delete': 'Delete',
  'common.remove': 'Remove',
  'common.add': 'Add',
  'common.back': 'Back',
  'common.close': 'Close',
  'common.retry': 'retry',
  'common.loading': 'Loading…',
  'common.somethingWrong': 'Something went wrong. Try again.',
  'common.you': '(you)',
  'common.confirm': 'Confirm',

  'nav.home': 'Home',
  'nav.chats': 'Chats',
  'nav.people': 'Contacts',
  'nav.you': 'Settings',

  'auth.email': 'Email',
  'auth.phone': 'Phone number',
  'auth.password': 'Password',
  'auth.yourName': 'Your name',
  'auth.signIn': 'Sign in',
  'auth.createAccount': 'Create account',
  'auth.haveAccount': 'I already have an account',
  'auth.newAccount': 'Create a new account',
  'auth.forgotPassword': 'Forgot your password?',
  'auth.backToSignIn': 'Back to sign in',
  'auth.noAccount': 'New to Bella?',
  'auth.hasAccount': 'Already have an account?',
  'auth.signUpNow': 'Create one',
  'auth.signInNow': 'Sign in',
  'auth.resetIntro': 'Enter your email and we’ll send you a link to reset your password.',
  'auth.sendResetLink': 'Send reset link',
  'auth.resetSent': 'If that address has an account, a reset link is on its way. Check your email.',
  'auth.checkEmail': 'Check your email for a confirmation link, then sign in.',
  'auth.continueWithGoogle': 'Continue with Google',
  'auth.continueWithApple': 'Continue with Apple',
  'auth.continueWithFacebook': 'Continue with Facebook',
  'auth.continueWithMicrosoft': 'Continue with Microsoft',
  'auth.or': 'or',
  'auth.continue': 'Continue',
  'auth.smsNotice': 'We’ll text you a 6-digit code. Standard rates apply.',
  'auth.codeSentTo': 'Enter the code sent to {phone}',
  'auth.verify': 'Verify',
  'auth.differentNumber': 'Use a different number',
  'auth.terms': 'By continuing you agree to the Terms and Privacy Policy.',
  'auth.passwordMin': 'At least 8 characters',
  'auth.signingIn': 'Signing you in…',
  'auth.confirming': 'Confirming…',
  'auth.linkExpired': 'That link has expired or has already been used. Request a new one.',
  'auth.linkIncomplete': 'That link is missing information. Request a new one and try again.',
  'auth.linkStale': 'That sign-in link has expired. Please try again.',
  'auth.newPasswordTitle': 'Choose a new password',
  'auth.newPasswordIntro':
    'You are signed in as {email}. Setting a password here replaces the old one everywhere.',
  'auth.newPassword': 'New password',
  'auth.confirmPassword': 'Confirm password',
  'auth.passwordsDiffer': 'Those do not match.',
  'auth.savePassword': 'Save password',
  'auth.needResetLink':
    'This page needs a valid reset link. Request a new one and open it from your email.',
  'auth.providerDisabled':
    'That sign-in method is not enabled on this server yet. Use email for now.',
  'auth.badCredentials': 'That email and password do not match an account.',
  'auth.alreadyRegistered': 'An account with that email already exists. Try signing in instead.',
  'auth.passwordTooShort': 'Please choose a password of at least 8 characters.',
  'auth.offline': 'Cannot reach the server. Check your connection and try again.',

  'home.title': 'Home',
  'home.composerPlaceholder': 'What’s happening?',
  'home.post': 'Post',
  'home.visibility': 'Who can see this',
  'home.visibilityFriends': 'Friends',
  'home.visibilityPublic': 'Everyone',
  'home.visibilityPrivate': 'Only me',
  'home.emptyTitle': 'Your feed is quiet',
  'home.emptyHint': 'Follow a few people and their posts will show up here.',
  'home.findPeople': 'Find people',
  'home.removeImage': 'Remove image',

  'post.title': 'Post',
  'post.commentsHeading': '{count} comments',
  'post.noComments': 'No comments yet.',
  'post.beFirst': 'Be the first to comment.',
  'post.commentPlaceholder': 'Write a comment…',
  'post.send': 'Send',
  'post.deleteComment': 'Delete comment',
  'post.deleteCommentConfirm': 'Delete this comment?',
  'post.notFound': 'Post not found',
  'post.notFoundHint': 'It may have been deleted, or you may not be allowed to see it.',
  'post.backHome': 'Back to home',
  'post.like': 'Like',
  'post.comment': 'Comment',

  'chats.title': 'Chats',
  'chats.newGroup': 'New group',
  'chats.newChat': 'New chat',
  'chats.emptyTitle': 'No conversations yet',
  'chats.emptyHint': 'Search for someone by name or phone number to start chatting.',
  'chats.sayHello': 'Say hello',
  'chats.photo': '📷 Photo',
  'chats.video': '🎬 Video',
  'chats.file': '📎 File',
  'chats.message': 'Message',
  'chats.typing': '{names} typing…',
  'chats.members': '{count} members',
  'chats.deletedMessage': 'This message was deleted',
  'chats.sending': 'sending',
  'chats.edited': 'edited',
  'chats.replyingTo': 'Replying to {name}',
  'chats.cancelReply': 'Cancel reply',
  'chats.originalMessage': 'Original message',
  'chats.messageActions': 'Message actions',
  'chats.reply': 'Reply',
  'chats.copyText': 'Copy text',
  'chats.reactWith': 'React with {emoji}',
  'chats.voiceCall': 'Voice call',
  'chats.videoCall': 'Video call',
  'chats.send': 'Send',
  'chats.attach': 'Attach',

  'voice.record': 'Record a voice message',
  'voice.stopAndSend': 'Stop and send',
  'voice.readyToSend': 'Ready to send',
  'voice.play': 'Play',
  'voice.pause': 'Pause',
  'voice.message': '🎤 Voice message',
  'voice.permissionDenied': 'Bella needs microphone permission to record.',
  'voice.unsupported': 'This browser cannot record audio.',

  // --- shared media ---
  'media.title': 'Media and files',
  'media.tabMedia': 'Media',
  'media.tabFiles': 'Files',
  'media.emptyMediaTitle': 'No media yet',
  'media.emptyMediaHint': 'Images and videos shared in this conversation will show up here.',
  'media.emptyFilesTitle': 'No files yet',
  'media.emptyFilesHint': 'Files shared in this conversation will show up here.',
  'media.file': 'File',
  'media.previous': 'Previous',
  'media.next': 'Next',
  'media.viewFullscreen': 'View fullscreen',

  'info.sharedMedia': 'Media and files',
  'info.groupTitle': 'Group info',
  'info.chatTitle': 'Chat info',
  'info.directMessage': 'Direct message',
  'info.viewProfile': 'View profile',
  'info.groupName': 'Group name',
  'info.notifications': 'Notifications',
  'info.notifyOn': 'You get notified about new messages.',
  'info.mutedUntil': 'Muted until {time}',
  'info.unmute': 'Unmute',
  'info.mute8h': 'Mute 8 hours',
  'info.mute1w': 'Mute 1 week',
  'info.membersHeading': '{count} members',
  'info.makeAdmin': 'Make admin',
  'info.demote': 'Demote',
  'info.addPeople': 'Add people',
  'info.searchPeople': 'Search by name or phone number',
  'info.allAlreadyIn': 'Everyone found is already in this group.',
  'info.noOneFound': 'No one found.',
  'info.leaveGroup': 'Leave group',
  'info.leaveConfirm': 'Leave this group? You will stop receiving its messages.',
  'info.ownerCannotLeave':
    'You created this group, so you cannot leave it — that would leave nobody able to manage it.',
  'info.removeConfirm': 'Remove {name} from the group?',
  'info.blockPerson': 'Block {name}',
  'info.blockConfirm': 'Block {name}? They will no longer be able to find or message you.',
  'info.notFound': 'Conversation not found',
  'info.notFoundHint': 'It may have been deleted, or you may have left it.',
  'info.backToChats': 'Back to chats',
  'info.roleOwner': 'Owner',
  'info.roleAdmin': 'Admin',

  'group.newTitle': 'New group',
  'group.create': 'Create',
  'group.namePlaceholder': 'Group name',
  'group.addPlaceholder': 'Add people by name or phone',
  'group.removePerson': 'Remove {name}',

  'friends.add': 'Add friend',
  'friends.accept': 'Accept',
  'friends.decline': 'Decline',
  'friends.requestSent': 'Request sent',
  'friends.areFriends': 'Friends',
  'friends.unfriend': 'Remove friend',
  'friends.unfriendConfirm': 'Remove {name} from your friends?',
  'friends.requestsTitle': 'Friend requests ({n})',
  'friends.yoursTitle': 'Your friends',
  'friends.yoursTitleCount': 'Your friends ({n})',
  'friends.searchResults': 'Search results',
  'friends.noneTitle': 'No friends yet',
  'friends.noneHint':
    'Search by name, @username or phone number to send your first friend request.',
  'friends.mutualCount': '{n} mutual friends',
  'friends.mutualCountOne': '1 mutual friend',
  'friends.mutualCountZero': 'No mutual friends',

  'people.title': 'Contacts',
  'people.searchPlaceholder': 'Name, @username or phone number',
  'people.phoneNotice': 'Phone numbers match exactly, and only for people who allow it.',
  'people.noneTitle': 'No one found',
  'people.noneHintPhone':
    'Either nobody uses that number on Bella, or they have turned off phone discovery.',
  'people.noneHintName': 'Try a different spelling, or search by their @username.',
  'people.matchedByPhone': 'matched by phone',
  'people.message': 'Message',
  'people.opening': 'Opening…',

  'profile.message': 'Message',
  'profile.call': 'Call',
  'profile.video': 'Video',
  'profile.noPostsTitle': 'No posts yet',
  'profile.noPostsHint': 'When this person posts, it will show up here.',
  'profile.noPostsStrangerHint':
    'Most posts are for friends only. Add them as a friend to see more.',
  'profile.noPostsYouTitle': 'You have not posted yet',
  'profile.noPostsYouHint': 'Anything you post will show up here.',
  'profile.block': 'Block',
  'profile.editProfile': 'Edit profile',
  'profile.unavailable': 'This person is not available.',

  'settings.title': 'Settings',
  'settings.changePhoto': 'Change photo',
  'settings.displayName': 'Display name',
  'settings.username': 'Username',
  'settings.usernamePlaceholder': 'Choose a username',
  'settings.usernameHint': '3–24 characters: letters, numbers and underscores.',
  'settings.bio': 'Bio',
  'settings.usernameTaken': 'That username is already taken.',
  'settings.couldNotSave': 'Could not save.',
  'settings.privacy': 'Privacy',
  'settings.findableByPhone': 'Findable by phone number',
  'settings.findableByPhoneOn': 'People who know {phone} can find you.',
  'settings.findableByPhoneOff': 'Add a phone number to use this.',
  'settings.findableByUsername': 'Findable by username',
  'settings.findableByUsernameHint': 'People can search for your @username.',
  'settings.password': 'Password',
  'settings.changePassword': 'Change password',
  'settings.newPasswordPlaceholder': 'New password, at least 8 characters',
  'settings.confirmPasswordPlaceholder': 'Confirm new password',
  'settings.passwordChanged': 'Password changed.',
  'settings.couldNotChange': 'Could not change it.',
  'settings.signInMethods': 'Ways to sign in',
  'settings.signInMethodsHint':
    'Keep at least two. If you lose access to one account, the other still gets you in.',
  'settings.methodEmail': 'Email and password',
  'settings.linkedAs': 'Linked as {who}',
  'settings.addMethod': 'Add',
  'settings.removeMethod': 'Unlink',
  'settings.removeMethodConfirm': 'Unlink {name}? You will no longer be able to sign in that way.',
  'settings.lastMethodWarning':
    'This is your only way to sign in. Add another before removing it, or you will be locked out.',
  'settings.setPasswordFirst':
    'You have no password set. Add one so you have a way back in if you lose your social account.',
  'settings.blocked': 'Blocked',
  'settings.blockedHint': 'They cannot find you, message you or call you.',
  'settings.unblock': 'Unblock',
  'settings.notifications': 'Notifications',
  'settings.notificationsOn': 'Turn on notifications',
  'settings.notificationsOff': 'Turn off notifications',
  'settings.notificationsIos':
    'On iPhone and iPad, add Bella to your Home Screen first — Safari only allows notifications for installed apps.',
  'settings.notificationsUnsupported': 'This browser does not support push notifications.',
  'settings.notificationsBlocked':
    'Notifications are blocked. Re-enable them in your browser’s site settings.',
  'settings.signOut': 'Sign out',
  'settings.language': 'Language',
  'settings.appearance': 'Appearance',
  'theme.light': 'Light',
  'theme.dark': 'Dark',

  'install.title': 'Install Bella',
  'install.hint': 'Add it to your home screen to open faster and get notifications.',
  'install.button': 'Install app',
  'install.installed': 'Bella is installed on this device.',
  'install.iosTitle': 'Install on iPhone or iPad',
  'install.iosStep1': 'Tap the Share button in Safari’s bottom bar.',
  'install.iosStep2': 'Choose “Add to Home Screen”.',
  'install.dismiss': 'Not now',

  'call.ringing': 'Ringing…',
  'call.connecting': 'Connecting…',
  'call.preparing': 'Getting ready…',
  'call.ended': 'Call ended',
  'call.incomingVideo': 'Incoming video call',
  'call.incomingVoice': 'Incoming voice call',
  'call.accept': 'Accept',
  'call.decline': 'Decline',
  'call.mute': 'Mute',
  'call.unmute': 'Unmute',
  'call.stopVideo': 'Stop video',
  'call.startVideo': 'Start video',
  'call.shareScreen': 'Share screen',
  'call.endCall': 'End call',
  'call.unavailable': 'This call is no longer available.',
  'call.nobodyElse': 'There is nobody else in this conversation.',
  'call.needsPermission': 'Bella needs microphone and camera permission to place a call.',
  'call.couldNotConnect':
    'Could not connect the call. One side’s network may be blocking a direct connection.',

  'update.ready': 'A new version of Bella is ready.',
  'update.later': 'Later',
  'update.reload': 'Reload',

  'presence.online': 'online',
  'presence.away': 'away',
  'presence.offline': 'offline',
}

export const DICTIONARIES = { vi, en } as const
export type Lang = keyof typeof DICTIONARIES

export const LANGUAGE_NAMES: Record<Lang, string> = {
  vi: 'Tiếng Việt',
  en: 'English',
}
